import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Users, Filter, RotateCcw, Sparkles } from "lucide-react";

function Toggle({ on, onChange, testid }) {
  return (
    <button onClick={() => onChange(!on)} data-testid={testid}
      className={`relative w-12 h-7 rounded-full border-2 border-black transition-colors ${on ? "bg-[#86EFAC]" : "bg-slate-200"}`}>
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white border-2 border-black rounded-full transition-transform ${on ? "translate-x-5" : ""}`} />
    </button>
  );
}

function daysLeft(iso) {
  if (!iso) return null;
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000));
}

export default function OpenSchoolRoster() {
  const [filters, setFilters] = useState({ countries: [], boards: [], grades: [], districts: [] });
  const [country, setCountry] = useState("");
  const [board, setBoard] = useState("");
  const [grade, setGrade] = useState("");
  const [district, setDistrict] = useState("");
  const [freeOnly, setFreeOnly] = useState(false);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/open-school/roster/filters").then(r => setFilters(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (country) params.country_code = country;
      if (board) params.board_code = board;
      if (grade) params.grade = grade;
      if (district) params.district = district;
      if (freeOnly) params.free_only = true;
      const r = await api.get("/open-school/roster", { params });
      setRows(r.data.students); setCount(r.data.count);
    } catch { toast.error("Failed to load roster"); }
    finally { setLoading(false); }
  }, [country, board, grade, district, freeOnly]);

  useEffect(() => { load(); }, [load]);

  const toggleFree = async (u, val) => {
    try {
      await api.patch(`/open-school/roster/${u.user_id}/free-override`, { free_override: val });
      setRows(rs => rs.map(r => r.user_id === u.user_id
        ? { ...r, free_override: val, subscription_status: val ? "free_override" : "trial", access_active: val || r.access_active }
        : r));
      toast.success(val ? `Free access granted to ${u.name}` : `Free access removed for ${u.name}`);
    } catch { toast.error("Could not update override"); }
  };

  const reset = () => { setCountry(""); setBoard(""); setGrade(""); setDistrict(""); setFreeOnly(false); };

  return (
    <div className="space-y-4" data-testid="open-school-roster">
      <div className="flex items-center gap-2">
        <Users size={20} />
        <h2 className="display text-xl font-bold">Open Schooling Roster</h2>
        <span className="nb-badge bg-[#93C5FD] text-xs ml-1">{count} students</span>
      </div>

      <div className="nb-card p-4 bg-white border-2 border-black space-y-3">
        <div className="flex items-center gap-2 text-xs font-bold uppercase text-slate-600"><Filter size={14} /> Filters</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <select className="nb-select" value={country} onChange={e => setCountry(e.target.value)} data-testid="roster-filter-country">
            <option value="">All Countries</option>
            {filters.countries.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="nb-select" value={board} onChange={e => setBoard(e.target.value)} data-testid="roster-filter-board">
            <option value="">All Boards</option>
            {filters.boards.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
          <select className="nb-select" value={grade} onChange={e => setGrade(e.target.value)} data-testid="roster-filter-grade">
            <option value="">All Grades</option>
            {filters.grades.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          <select className="nb-select" value={district} onChange={e => setDistrict(e.target.value)} data-testid="roster-filter-district">
            <option value="">All Districts</option>
            {filters.districts.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <label className="flex items-center gap-2 text-sm font-bold">
            <input type="checkbox" checked={freeOnly} onChange={e => setFreeOnly(e.target.checked)} data-testid="roster-filter-freeonly" />
            Free-access only
          </label>
          <button className="nb-btn nb-btn-ghost text-xs" onClick={reset} data-testid="roster-reset"><RotateCcw size={14} /> Reset</button>
        </div>
      </div>

      <div className="nb-card border-2 border-black overflow-x-auto">
        <table className="nb-table w-full text-sm">
          <thead>
            <tr>
              <th>Open ID</th><th>Name</th><th>Board</th><th>Grade</th>
              <th>Location</th><th>Trial</th><th>Status</th><th className="text-center">Free Override</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const left = daysLeft(r.trial_expires_at);
              return (
                <tr key={r.user_id} data-testid={`roster-row-${r.user_id}`}>
                  <td className="mono text-xs">{r.open_student_id}</td>
                  <td><div className="font-bold">{r.name}</div><div className="text-xs text-slate-500">{r.email}</div></td>
                  <td>{r.board_code}</td>
                  <td>{r.grade}</td>
                  <td className="text-xs">{r.city}, {r.state}<div className="text-slate-400">{r.district}</div></td>
                  <td className="text-xs">{r.free_override ? "—" : `${left}d left`}</td>
                  <td>
                    <span className={`nb-badge text-xs ${r.free_override ? "bg-[#86EFAC]" : r.access_active ? "bg-[#FDE047]" : "bg-[#FCA5A5]"}`}>
                      {r.free_override ? <><Sparkles size={11} /> FREE</> : r.access_active ? "TRIAL" : "EXPIRED"}
                    </span>
                  </td>
                  <td className="text-center">
                    <Toggle on={r.free_override} onChange={v => toggleFree(r, v)} testid={`roster-toggle-${r.user_id}`} />
                  </td>
                </tr>
              );
            })}
            {!rows.length && (
              <tr><td colSpan={8} className="text-center text-slate-500 py-6">
                {loading ? "Loading…" : "No open-school students match these filters yet."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
